import { useQuery } from "@tanstack/react-query";
import { Inbox, RotateCcw } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/Button";
import { useAuth } from "@/app/auth/context";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { SelectField, TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { ArchiveEmptyState } from "@/features/archive/ArchiveEmptyState";
import { LoadMore, PageSizeSelect } from "@/components/ui/Paging";
import { formatDateTime, formatNumber } from "@/lib/format";
import { stepFocus, useShortcuts } from "@/lib/keyboard";
import {
  filtersFromParams,
  LIST_SIZES,
  paramsFromFilters,
  ticketsApi,
  withSubqueueCounts,
  type TicketFilters,
  type TicketSort,
} from "@/lib/tickets-api";
import { readPerPage, rememberPerPage, usePaged, type PerPage } from "@/lib/use-paged";

import { SavedFilters } from "./SavedFilters";
import { StateBadge } from "./TicketBits";

const SORTS: TicketSort[] = ["created_desc", "created_asc", "changed_desc", "number_desc"];

/** Text fields are applied on submit; their draft is reset whenever the applied filters change. */
function FilterForm({
  filters,
  onApply,
}: {
  filters: TicketFilters;
  onApply: (next: TicketFilters) => void;
}) {
  const { t } = useTranslation();
  const queues = useQuery({
    queryKey: ["queues"],
    queryFn: ticketsApi.queues,
    staleTime: 60_000,
    select: withSubqueueCounts,
  });
  const facets = useQuery({ queryKey: ["tickets", "facets"], queryFn: ticketsApi.facets, staleTime: 60_000 });
  const owners = useQuery({ queryKey: ["tickets", "owners"], queryFn: ticketsApi.owners, staleTime: 60_000 });
  const [text, setText] = useState(filters.q ?? "");
  const [customer, setCustomer] = useState(filters.customer ?? "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply({ ...filters, q: text.trim() || undefined, customer: customer.trim() || undefined });
  };
  const set = (patch: Partial<TicketFilters>) => onApply({ ...filters, ...patch });

  return (
    <form onSubmit={submit} className="space-y-4 border-b border-line p-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          label={t("tickets.filters.text")}
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("tickets.filters.textPlaceholder")}
        />
        <SelectField
          label={t("tickets.filters.queue")}
          value={filters.queue ?? ""}
          onChange={(e) => set({ queue: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">{t("tickets.filters.allQueues")}</option>
          {queues.data?.map((queue) => (
            <option key={queue.id} value={queue.id}>
              {"— ".repeat(queue.depth)}
              {queue.name} ({formatNumber(queue.tickets)})
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t("tickets.filters.state")}
          value={filters.states?.[0] ?? ""}
          onChange={(e) => set({ states: e.target.value ? [Number(e.target.value)] : [] })}
        >
          <option value="">{t("tickets.filters.allStates")}</option>
          {facets.data?.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name} ({formatNumber(state.tickets)})
            </option>
          ))}
        </SelectField>
        <TextField
          label={t("tickets.filters.customer")}
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          placeholder={t("tickets.filters.customerPlaceholder")}
        />
        <SelectField
          label={t("tickets.filters.owner")}
          value={filters.owner ?? ""}
          onChange={(e) => set({ owner: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">{t("tickets.filters.allOwners")}</option>
          {owners.data?.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name} ({formatNumber(owner.tickets)})
            </option>
          ))}
        </SelectField>
        <TextField
          label={t("tickets.filters.from")}
          type="date"
          value={filters.from ?? ""}
          onChange={(e) => set({ from: e.target.value || undefined })}
        />
        <TextField
          label={t("tickets.filters.to")}
          type="date"
          value={filters.to ?? ""}
          onChange={(e) => set({ to: e.target.value || undefined })}
        />
        <SelectField
          label={t("tickets.filters.sort")}
          value={filters.sort ?? "created_desc"}
          onChange={(e) => set({ sort: e.target.value as TicketSort })}
        >
          {SORTS.map((sort) => (
            <option key={sort} value={sort}>
              {t(`tickets.sorts.${sort}`)}
            </option>
          ))}
        </SelectField>
        <div className="flex items-end gap-2">
          <Button type="submit">{t("tickets.filters.apply")}</Button>
          <Button
            variant="ghost"
            onClick={() => {
              setText("");
              setCustomer("");
              onApply({});
            }}
          >
            <RotateCcw className="size-4" aria-hidden="true" />
            {t("tickets.filters.reset")}
          </Button>
        </div>
      </div>
    </form>
  );
}

const PER_PAGE_KEY = "tv.tickets.perPage";
const ALL_CHUNK = 500;

export function TicketsPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);
  const per = readPerPage(params.get("per"), LIST_SIZES, PER_PAGE_KEY, 50);
  const page = Math.max(0, Number(params.get("page") ?? "1") - 1) || 0;
  const filterKey = paramsFromFilters(filters).toString();
  const list = usePaged(
    ["tickets", "list", filterKey],
    (offset, limit) => ticketsApi.list(filters, offset, limit),
    per,
    page,
    ALL_CHUNK,
  );
  const { total, offset, size } = list;
  // j / k move through the rows, Enter opens the focused one.
  const links = useRef<(HTMLAnchorElement | null)[]>([]);
  useShortcuts({ j: () => stepFocus(links.current, 1), k: () => stepFocus(links.current, -1) });
  const here = `/tickets${params.size ? `?${params.toString()}` : ""}`;
  const numbers = list.items?.map((item) => item.number);

  const withPaging = (next: URLSearchParams, target: number, perPage: PerPage) => {
    if (perPage !== 50) next.set("per", String(perPage));
    if (perPage !== "all" && target > 0) next.set("page", String(target + 1));
    return next;
  };
  const apply = (next: TicketFilters) => setParams(withPaging(paramsFromFilters(next), 0, per));
  const goTo = (target: number) => setParams(withPaging(paramsFromFilters(filters), target, per));
  const choosePer = (value: PerPage) => {
    rememberPerPage(PER_PAGE_KEY, value);
    setParams(withPaging(paramsFromFilters(filters), 0, value));
  };

  return (
    <>
      <PageHeader title={t("nav.tickets")} />
      <Card className="overflow-hidden">
        {/* Remount the form when the filters change from outside (sidebar, back button). */}
        <FilterForm key={filterKey} filters={filters} onApply={apply} />
        <SavedFilters
          current={filterKey}
          onApply={(query) => setParams(withPaging(new URLSearchParams(query), 0, per))}
        />
        {me?.data_scope === "none" ? (
          <ArchiveEmptyState />
        ) : list.items && list.items.length === 0 ? (
          <EmptyState icon={Inbox} title={t("tickets.emptyTitle")}>
            {t("tickets.emptyBody")}
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="stack-table w-full min-w-[56rem] text-left text-sm">
              <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
                <tr>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.ticket")}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.queue")}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.state")}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.owner")}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.customer")}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    {t("tickets.columns.created")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.items?.map((ticket, index) => (
                  <tr key={ticket.id} className="align-top hover:bg-surface-2">
                    <td className="px-5 py-3">
                      <Link
                        ref={(element) => {
                          links.current[index] = element;
                        }}
                        to={`/tickets/${encodeURIComponent(ticket.number)}`}
                        state={{ from: here, numbers }}
                        className="font-medium text-ink underline-offset-4 hover:text-primary hover:underline"
                      >
                        <span className="text-ink-muted tabular-nums">#{ticket.number}</span>{" "}
                        {ticket.title || t("tickets.untitled")}
                      </Link>
                      <div className="text-xs text-ink-subtle">
                        {t("tickets.messages", { count: ticket.article_count })}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-muted" data-label={t("tickets.columns.queue")}>
                      {ticket.queue}
                    </td>
                    <td className="px-5 py-3" data-label={t("tickets.columns.state")}>
                      <StateBadge name={ticket.state} category={ticket.state_category} />
                    </td>
                    <td className="px-5 py-3 text-ink-muted" data-label={t("tickets.columns.owner")}>
                      {ticket.owner || "—"}
                    </td>
                    <td
                      className="px-5 py-3 break-all text-ink-muted"
                      data-label={t("tickets.columns.customer")}
                    >
                      {ticket.customer_user || ticket.customer_key || "—"}
                    </td>
                    <td
                      className="px-5 py-3 whitespace-nowrap text-ink-muted tabular-nums"
                      data-label={t("tickets.columns.created")}
                    >
                      {formatDateTime(ticket.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
          {per === "all" ? (
            <LoadMore
              loaded={list.items?.length ?? 0}
              total={total}
              hasMore={list.hasMore}
              loading={list.loadingMore}
              onLoad={list.loadMore}
            />
          ) : (
            <span className="text-ink-muted tabular-nums" aria-live="polite">
              {total > 0
                ? t("tickets.range", {
                    from: formatNumber(offset + 1),
                    to: formatNumber(Math.min(offset + size, total)),
                    total: formatNumber(total),
                  })
                : t("tickets.none")}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <PageSizeSelect value={per} sizes={LIST_SIZES} onChange={choosePer} />
            {per !== "all" && (
              <>
                <Button variant="secondary" disabled={page === 0} onClick={() => goTo(page - 1)}>
                  {t("common.previous")}
                </Button>
                <Button variant="secondary" disabled={offset + size >= total} onClick={() => goTo(page + 1)}>
                  {t("common.next")}
                </Button>
              </>
            )}
          </div>
        </footer>
      </Card>
    </>
  );
}
